import { usePresence } from '../../../../hooks/use-presence';
import { ModalBase } from '../../components';
import { useSlaBreaches } from '../../hooks/use-sla';

import { adaptBreach } from './tela5-adapter';

import type { SlaRowVM } from '../../view-models';
import type React from 'react';

/**
 * T5-2 drill-down — modal de violações reais paginadas de um SLA.
 * Lista vem de GET /v1/sla/dashboard/breaches (cursor). Nomes de operador via
 * presence (degradação honesta para '—' quando sem assignedToUserId).
 */
export function SlaDrillDownModal({
  sla,
  onClose,
}: {
  sla: SlaRowVM;
  onClose: () => void;
}): React.JSX.Element {
  const { data: presence } = usePresence();
  const { rows, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useSlaBreaches(sla.id);

  const nameFor = (userId: string): string | undefined =>
    presence.find((p) => p.userId === userId)?.name ?? undefined;

  const breaches = rows.map((b) => adaptBreach(b, nameFor));

  return (
    <ModalBase
      open
      onClose={onClose}
      title={`Violações · ${sla.label}`}
      width={620}
      footer={
        <div style={{ marginLeft: 'auto' }}>
          <button type="button" className="btn-ghost-mini" onClick={onClose}>
            Fechar
          </button>
        </div>
      }
    >
      <div className="modal-form">
        <div className="modal-form__alert-head">
          <span style={{ marginLeft: 'auto' }} className="mono">
            SLA: {sla.slaLabel}
          </span>
        </div>
        <div>
          Cumprimento {sla.cumprimento}% · {sla.total} demandas · {sla.violations} violações
        </div>

        {isError && (
          <div className="banner banner--danger" role="alert">
            <div className="banner__body">
              <span className="banner__title">Falha ao carregar as violações.</span>
            </div>
          </div>
        )}

        {!isError && isLoading && <div style={{ color: 'var(--fg-3)' }}>Carregando violações…</div>}

        {!isError && !isLoading && breaches.length === 0 && (
          <div style={{ color: 'var(--fg-3)' }}>Nenhuma violação registrada para este SLA.</div>
        )}

        {breaches.length > 0 && (
          <ul className="sla-drill-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {breaches.map((b) => (
              <li
                key={b.id}
                className="sla-drill-row"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  padding: 'var(--space-2) 0',
                  borderBottom: '1px solid var(--border-default)',
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  {b.resourceLabel} · {b.severityLabel} · atendente {b.opName} · há{' '}
                  {b.occurredAtLabel} · {b.durationLabel}
                </span>
                <span
                  className="chip"
                  style={{
                    background: b.resolved ? 'var(--success-bg)' : 'var(--warning-bg)',
                    color: b.resolved ? 'var(--success-fg)' : 'var(--warning-fg)',
                    borderColor: b.resolved ? 'var(--success-border)' : 'var(--warning-border)',
                  }}
                >
                  {b.resolved ? 'resolvido' : 'aberto'}
                </span>
              </li>
            ))}
          </ul>
        )}

        {hasNextPage && (
          <button
            type="button"
            className="btn-ghost-mini"
            disabled={isFetchingNextPage}
            onClick={fetchNextPage}
          >
            {isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
          </button>
        )}
      </div>
    </ModalBase>
  );
}
